import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import ru from '../translations/ru';
import en from '../translations/en';

const LanguageContext = createContext();

const languages = {
  ru: ru,
  en: en
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ru');
  const [translations, setTranslations] = useState(ru);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Detect user's preferred language
  useEffect(() => {
    const detectLanguage = () => {
      // Check localStorage first
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && languages[savedLanguage]) {
        return savedLanguage;
      }

      // Check browser language
      const browserLanguage = navigator.language || navigator.languages[0];
      if (browserLanguage && browserLanguage.startsWith('ru')) {
        return 'ru';
      }

      // Default to English
      return 'en';
    };

    const detectedLanguage = detectLanguage();
    setLanguage(detectedLanguage);
    setTranslations(languages[detectedLanguage]);
  }, []);

  // Update language when it changes in localStorage (multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'language' && languages[e.newValue] && e.newValue !== language) {
        setLanguage(e.newValue);
        setTranslations(languages[e.newValue]);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [language]);

  // Change language
  const changeLanguage = useCallback((newLanguage) => {
    if (languages[newLanguage] && newLanguage !== language) {
      setLanguage(newLanguage);
      setTranslations(languages[newLanguage]);
      localStorage.setItem('language', newLanguage);
    }
  }, [language]);

  // Get translation
  const t = useCallback((key, params) => {
    let translation = translations[key] || key;
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(new RegExp(`{{${paramKey}}}`, 'g'), value);
      });
    }
    return translation;
  }, [translations]);

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t
  }), [language, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
